<script setup lang="ts">
import { useSeoMeta } from "@unhead/vue";
import { HTTPError } from "ky";
import { type Locale } from "shared-core/services/i18n.ts";
import type {
  PublicRiskAnalysis,
  SecuritySolution,
  SecuritySolutionsResponse,
} from "shared-core/types/risk-analysis.ts";
import { formatCurrency } from "shared-core/utils/common.ts";
import { convertProductivitySuiteToIntegrationName } from "shared-core/utils/productivity-suite-util.ts";
import {
  translateTaskDescription,
  translateTaskTitle,
} from "shared-core/utils/task-translation.ts";
import integrateSystemsIllustration from "shared-ui/assets/illustrations/integrate-systems.avif";
import nodesIllustration from "shared-ui/assets/illustrations/nodes.svg";
import riskAnalysisHeroIllustration from "shared-ui/assets/illustrations/risk-analysis-hero.avif";
import breachCaseImage from "shared-ui/assets/images/risk-analysis-breach-case.avif";
import CTAButton from "shared-ui/components/buttons/CTAButton.vue";
import RiskScoreCard from "shared-ui/components/cards/risk-analysis/RiskScoreCard.vue";
import TopRisksTable from "shared-ui/components/risk-analysis/TopRisksTable.vue";
import { useI18nRuntime } from "shared-ui/services/vue-i18n.ts";
import { useRiskAnalysisStore } from "shared-ui/stores";
import type {
  ExpandedRisk,
  FormattedRisk,
} from "shared-ui/types/risk-analysis";
import { useApi } from "shared-ui/utils/api";
import { getTopRisks } from "shared-ui/utils/risk-analysis-utils";
import { getRouteParam, paramToString } from "shared-ui/utils/router-utils";
import { computed, type ComputedRef, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { router } from "@/router.ts";

const { t, locale } = useI18n();
const { ensureLocaleNamespaces } = useI18nRuntime();

const riskAnalysisStore = useRiskAnalysisStore();
const route = useRoute<"/risk-analysis/public/[domain]">();

definePage({
  meta: {
    requiresAuth: false,
  },
});

const api = useApi();

const analysis = ref<PublicRiskAnalysis | null>(null);
const topRisks = ref<FormattedRisk[]>([]);
const topRisksExpanded = ref<ExpandedRisk[]>([]);
const domain: ComputedRef<string> = computed(() =>
  getRouteParam(route.params.domain),
);
const submitEmail: ComputedRef<boolean> = computed(
  () => riskAnalysisStore.requireEmail,
);
const emailErrorMessage = ref<string>("");
const email = ref<string>("");
const emailOrPhoneRegex = /^(?:\+?\d{5,20}|[^\s@]+@(?:[^\s@.]+\.)+[^\s@.]+)$/;
const isEmailValid = computed(() => {
  return email.value.trim() !== "" && emailOrPhoneRegex.test(email.value);
});
const securitySolutions = ref<SecuritySolution[]>([]);

function validateEmail(): void {
  if (!isEmailValid.value) {
    emailErrorMessage.value = t(
      "risk_analysis.public_domain.invalid_email_phone",
    );
  } else {
    emailErrorMessage.value = "";
  }
}

useSeoMeta({
  title: () =>
    t("risk_analysis.public_domain.title", {
      domain: paramToString(domain.value),
    }),
});

onMounted(async () => {
  if (!domain.value) {
    await router.push({
      path: "/risk-analysis/public",
      query: { error: t("risk_analysis.public_domain.missing_domain") },
    });

    return;
  }

  analysis.value = riskAnalysisStore.analysis;

  // If we don't have analysis stored for this domain, fetch it from backend
  if (analysis.value === null || analysis.value.domain !== domain.value) {
    try {
      analysis.value = await riskAnalysisStore.fetchCachedDomainResult(
        domain.value,
      );
    } catch (error) {
      console.error(error);

      if (error instanceof HTTPError && error.response.status === 404) {
        await backToPublicRiskAnalysis(
          t("risk_analysis.public_domain.analysis_expired"),
        );

        return;
      }

      await backToPublicRiskAnalysis(
        error instanceof Error ? error.message : String(error),
      );

      return;
    }
  }

  try {
    await riskAnalysisStore.fetchConversionRate(
      navigator.languages[0] ?? navigator.language ?? "en-US",
    );
  } catch (error) {
    console.error("Failed to fetch conversion rate", error);
  }

  // Format productivity suite
  if (analysis.value.productivitySuite) {
    analysis.value.productivitySuite =
      convertProductivitySuiteToIntegrationName(
        analysis.value.productivitySuite,
      );
  }

  [topRisks.value, topRisksExpanded.value] = getTopRisks(
    analysis.value.risks.slice(0, 5),
    analysis.value.companySize,
    riskAnalysisStore.currencyConversion,
  );

  try {
    const securitySolutionsParams = new URLSearchParams();
    securitySolutionsParams.append(
      "productivitySuite",
      analysis.value.productivitySuite ?? "unknown",
    );
    const securitySolutionsResponse = await api.get<SecuritySolutionsResponse>(
      `public/risk-analysis/security-solutions?${securitySolutionsParams.toString()}`,
    );
    securitySolutions.value = (
      await securitySolutionsResponse.data
    ).securitySolutions;
  } catch (error) {
    console.error(error);
  }

  await ensureLocaleNamespaces(locale.value as Locale, ["common", "tasks"]);
});

async function backToPublicRiskAnalysis(error: string): Promise<void> {
  await router.push({
    path: "/risk-analysis/public",
    query: {
      error: error,
      domain: domain.value,
    },
  });
}

async function onSubmit(): Promise<void> {
  try {
    await api.post("public/risk-analysis/email", {
      json: {
        emailOrPhone: email.value,
      },
    });
    riskAnalysisStore.setRequireEmail(false);
    emailErrorMessage.value = "";
  } catch (error) {
    // Only show error for bad request, let through all other kind of errors in order to not trap users in the dialogue
    if (error instanceof HTTPError && error.response.status === 400) {
      emailErrorMessage.value = t(
        "risk_analysis.public_domain.invalid_email_phone",
      );
    } else {
      riskAnalysisStore.setRequireEmail(false);
      console.error(
        "Failed to post email/phone number for risk analysis",
        error,
      );
    }
  }
}
</script>

<template>
  <RiskAnalysisResultTemplate>
    <template v-if="analysis !== null" #main>
      <div
        v-if="submitEmail"
        class="absolute inset-0 z-998 backdrop-blur-xs"
      ></div>
      <div
        v-if="submitEmail"
        class="fixed inset-0 z-999 inline-flex items-center justify-center"
      >
        <div
          class="inline-grid grid-cols-1 gap-9 rounded-3xl bg-white p-12 md:grid-cols-2"
        >
          <img
            alt=""
            :src="riskAnalysisHeroIllustration"
            class="hidden h-auto rounded-3xl md:block md:max-w-xs lg:max-w-sm"
          />
          <div class="inline-flex max-w-md flex-col gap-4">
            <h2 class="text-3xl! font-normal!">
              {{ t("risk_analysis.public_domain.risk_analysis_ready") }}
            </h2>
            <p class="text-neutral-500">
              {{ t("risk_analysis.public_domain.enter_email_phone") }}
            </p>
            <form
              class="relative inline-flex w-full flex-col items-start justify-center overflow-hidden"
              @submit.prevent="onSubmit"
            >
              <div class="inline-flex w-full flex-col gap-4 md:flex-row">
                <div class="inline-flex w-full flex-col">
                  <div
                    class="relative inline-flex h-16 w-full flex-row items-center rounded-4xl border border-neutral-300 bg-white px-4 shadow-sm"
                  >
                    <input
                      v-model="email"
                      aria-label="Phone number or email"
                      name="email"
                      type="text"
                      :placeholder="
                        t('risk_analysis.public_domain.email_phone_placeholder')
                      "
                      class="mx-4 w-full focus:ring-2 focus:ring-brand-yellow focus:outline-none"
                      required
                      @blur="validateEmail"
                    />
                  </div>
                  <small
                    v-if="emailErrorMessage"
                    class="mt-1 block text-red-500"
                    >{{ emailErrorMessage }}</small
                  >
                </div>
                <button
                  type="submit"
                  :disabled="!isEmailValid"
                  class="h-16 w-auto cursor-pointer bg-gray-200 px-6 py-4 text-lg font-semibold transition"
                  :class="
                    isEmailValid
                      ? 'text-black! hover:bg-gray-400'
                      : 'text-black/40!'
                  "
                >
                  {{ t("risk_analysis.public_domain.continue") }}
                </button>
              </div>
            </form>
            <p class="text-xs text-neutral-600">
              {{ t("risk_analysis.public_domain.email_phone_agreement") }}
            </p>
          </div>
        </div>
      </div>

      <div class="hidden w-full flex-row items-center justify-between md:flex">
        <h1 class="text-3xl! font-normal text-primary-black!">
          {{ t("risk_analysis.public_domain.free_risk_analysis") }}
        </h1>
        <CTAButton
          :message="t('risk_analysis.public_domain.sign_up_for_free')"
          link-medium="risk_analysis"
          link-campaign="header"
        />
      </div>

      <div
        v-if="analysis.uncertainty"
        class="inline-flex w-full items-center justify-between overflow-hidden rounded-3xl bg-white/95 p-6 shadow-sm backdrop-blur-sm"
      >
        <div
          class="inline-flex flex-1 flex-col items-start justify-start gap-2"
        >
          <div
            class="justify-start self-stretch text-xl font-normal text-primary-black"
          >
            {{ t("risk_analysis.public_domain.potentially_inaccurate") }}
          </div>
          <div
            class="justify-start self-stretch text-base/normal font-normal text-neutral-500"
          >
            {{ t("risk_analysis.public_domain.multiple_companies") }}
            <a
              class="text-inherit underline"
              href="https://app.controla.ai?utm_source=website&utm_medium=risk_analysis&utm_campaign=inaccurate_result"
              target="_blank"
              >{{ t("risk_analysis.public_domain.sign_up_for_accurate") }}</a
            >.
          </div>
        </div>
        <button
          class="flex h-12 cursor-pointer items-center justify-center gap-1.5 rounded-action px-6 py-4 outline-2 -outline-offset-2 outline-neutral-500"
          @click="analysis.uncertainty = false"
        >
          <span class="justify-start text-base font-medium text-neutral-800">
            {{ t("risk_analysis.public_domain.got_it") }}
          </span>
        </button>
      </div>

      <div class="relative inline-flex w-full overflow-hidden rounded-3xl p-16">
        <CompanyProfile
          :hero-image-url="analysis.heroImageUrl"
          :favicon-url="analysis.faviconUrl"
          :domain="domain"
          :company-name="analysis.companyName"
          :industry="analysis.industry ?? null"
          :productivity-suite="analysis.productivitySuite"
          :company-size-range="analysis.companySizeRange"
        />
      </div>

      <div class="flex w-full flex-col gap-6 md:flex-row">
        <RiskScoreCard
          :value="analysis.riskScore"
          class="w-full rounded-3xl p-9"
        >
          <template #risk-score-explanation>
            <p class="text-base text-neutral-700">
              {{ analysis.riskScoreExplanation }}
            </p>
          </template>
        </RiskScoreCard>
        <Card class="w-full rounded-3xl p-9" pt:body:class="p-0">
          <template #title>
            <h2 class="text-2xl! font-normal! text-primary-black!">
              {{ t("risk_analysis.public_domain.potential_damage_per_breach") }}
            </h2>
          </template>
          <template #content>
            <p
              class="w-11/12 py-4 text-base text-neutral-700 md:w-full lg:w-11/12"
            >
              {{
                t("risk_analysis.public_domain.estimated_breach_cost_public")
              }}
            </p>
            <div
              class="inline-flex w-fit items-center justify-center rounded-4xl bg-zinc-100 px-6 py-5 lg:mb-5"
            >
              <p class="text-xl font-semibold text-primary-black">
                {{
                  formatCurrency(
                    Math.round(
                      (analysis.expectedBreachCostRange.lower *
                        riskAnalysisStore.currencyConversion.rate) /
                        1000,
                    ) * 1000,
                    riskAnalysisStore.currencyConversion.currency,
                  )
                }}
                -
                {{
                  formatCurrency(
                    Math.round(
                      (analysis.expectedBreachCostRange.upper *
                        riskAnalysisStore.currencyConversion.rate) /
                        1000,
                    ) * 1000,
                    riskAnalysisStore.currencyConversion.currency,
                  )
                }}
              </p>
            </div>
          </template>
        </Card>
      </div>

      <Banner
        image-alt=""
        :image-src="nodesIllustration"
        :header="t('risk_analysis.public_domain.how_did_we_get_numbers')"
      >
        <template #description>
          {{ t("risk_analysis.public_domain.analysis_explanation") }}
        </template>
        <template #cta>
          <CTAButton
            :message="t('risk_analysis.public_domain.read_more')"
            link="https://www.controla.ai/resources/behind-the-scenes-of-controlas-cyber-risk-model"
            link-medium="risk_analysis"
            link-campaign="read_more"
            link-class="!bg-transparent !text-primary-white hover:!bg-zinc-700"
          />
        </template>
      </Banner>

      <div
        class="inline-flex w-full flex-col gap-3 rounded-3xl bg-white p-4 sm:p-9"
      >
        <div class="inline-flex flex-row justify-between pb-4">
          <div class="inline-flex flex-col gap-3">
            <h2 class="text-2xl! font-normal!">
              {{ t("risk_analysis.public_domain.common_threats") }}
            </h2>
            <p class="text-sm text-neutral-600">
              {{ t("risk_analysis.public_domain.common_threats_description") }}
            </p>
          </div>
          <div class="hidden shrink-0">
            <CTAButton
              message="Sign up to fix all"
              link-medium="risk_analysis"
              link-campaign="common_threats"
            />
          </div>
        </div>
        <TopRisksTable
          v-model:top-risks-expanded="topRisksExpanded"
          :top-risks="topRisks"
          :company-size="analysis.companySize"
          :hide-savings="false"
        />
        <div class="hidden">
          <CTAButton
            message="Sign up to fix all"
            link-medium="risk_analysis"
            link-campaign="common_threats"
          />
        </div>
      </div>

      <div
        v-if="analysis.breachCase"
        class="grid w-full grid-cols-1 gap-9 rounded-3xl border-zinc-700 bg-zinc-800 p-9 lg:grid-cols-2"
      >
        <div class="flex flex-row justify-between gap-4">
          <div class="flex max-w-4xl flex-col justify-center gap-3">
            <h3 class="text-xl! font-normal! text-white!">
              {{ t("risk_analysis.public_domain.breach_case_industry") }}
            </h3>
            <h4 class="text-2xl! font-semibold! text-white!">
              {{ analysis.breachCase.title }}
            </h4>
            <p class="text-sm text-neutral-300">
              {{ analysis.breachCase.newsArticleSummary }}
            </p>
            <p
              v-if="analysis.breachCase.breachCost"
              class="font-secondary text-lg font-medium text-white"
            >
              {{ t("risk_analysis.public_domain.breach_cost") }}
              {{ analysis.breachCase.breachCost }}
            </p>
            <SourcePopover
              button-class="!text-primary-white text-left"
              :sources="[
                {
                  name: analysis.breachCase.title,
                  link: analysis.breachCase.newsArticleLink,
                },
              ]"
            />
          </div>
        </div>
        <div>
          <img
            :src="breachCaseImage"
            width="522"
            height="348"
            loading="lazy"
            class="h-auto w-full rounded-xl"
            alt=""
          />
        </div>
      </div>

      <div class="flex w-full flex-col gap-9 rounded-3xl bg-white p-9">
        <div class="flex flex-row justify-between gap-4">
          <div class="flex max-w-4xl flex-col gap-3">
            <h2 class="text-2xl! font-normal! text-primary-black!">
              {{ t("risk_analysis.public_domain.security_solutions") }}
            </h2>
            <p class="text-sm text-neutral-600">
              {{
                t("risk_analysis.public_domain.security_solutions_description")
              }}
            </p>
          </div>
          <div class="hidden shrink-0">
            <CTAButton
              message="Sign up to implement"
              link-medium="risk_analysis"
              link-campaign="security_solutions"
            />
          </div>
        </div>
        <div
          v-if="securitySolutions.length === 0"
          class="flex flex-col gap-5 md:flex-row"
        >
          <Skeleton v-for="idx in 5" :key="idx" width="14rem" height="14rem" />
        </div>
        <div v-else class="flex flex-col gap-5 xl:grid xl:grid-cols-5">
          <SolutionCard
            v-for="securitySolution in securitySolutions"
            :key="securitySolution.fixReference"
            :os-or-productivity-suite="securitySolution.osOrProductivitySuite"
            :title="translateTaskTitle(securitySolution, t)"
            :description="translateTaskDescription(securitySolution, t)"
          />
          <SolutionCard
            :title="t('risk_analysis.public_domain.sign_up_to_browse')"
            :description="
              t('risk_analysis.public_domain.browse_solutions_description')
            "
            :hide-icon="true"
          >
            <template #title>
              <a
                class="text-black"
                target="_blank"
                href="https://app.controla.ai/?utm_source=website&utm_medium=risk_analysis&utm_campaign=security_solutions"
              >
                {{ t("risk_analysis.public_domain.sign_up_to_browse") }}
              </a>
            </template>
          </SolutionCard>
        </div>
        <div class="hidden">
          <CTAButton
            :message="t('risk_analysis.public_domain.sign_up_to_implement')"
            link-medium="risk_analysis"
            link-campaign="security_solutions"
          />
        </div>
      </div>

      <Banner
        image-alt=""
        :image-src="integrateSystemsIllustration"
        :header="t('risk_analysis.public_domain.secure_your_company')"
      >
        <template #description>
          {{ t("risk_analysis.public_domain.secure_company_description") }}
        </template>
        <template #cta>
          <CTAButton
            :message="t('risk_analysis.public_domain.sign_up_for_free')"
            link-medium="risk_analysis"
            link-campaign="upgrade_to_pro_risk_analysis"
            link-class="!bg-white !text-primary-black"
          />
        </template>
      </Banner>
    </template>
  </RiskAnalysisResultTemplate>
</template>
